import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

export function Landing() {
  const navigate = useNavigate();
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    // Navbar scroll effect
    const handleScroll = () => {
      if (navRef.current && window.scrollY > 50) {
        navRef.current.classList.add('scrolled');
      } else if (navRef.current) {
        navRef.current.classList.remove('scrolled');
      }
    };

    window.addEventListener('scroll', handleScroll);

    // Fade in on scroll
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const fadeElements = document.querySelectorAll('.fade-in');
    fadeElements.forEach((el) => observer.observe(el));

    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, []);

  const handleLoginClick = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-white text-gray-900 relative">
      {/* Grid background */}
      <div className="fixed inset-0 pointer-events-none z-0" style={{
        backgroundImage: `
          linear-gradient(rgba(0, 0, 0, 0.03) 1px, transparent 1px),
          linear-gradient(90deg, rgba(0, 0, 0, 0.03) 1px, transparent 1px)
        `,
        backgroundSize: '48px 48px'
      }} />
      {/* Navigation */}
      <nav
        ref={navRef}
        className="fixed top-0 left-0 right-0 z-50 py-4 transition-all duration-300"
      >
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="flex items-center justify-between">
            <a href="#" className="flex items-center gap-2.5 no-underline">
              <div className="w-9 h-9 bg-gradient-to-br from-[#E85A24] to-[#FF7A45] rounded-lg flex items-center justify-center relative overflow-hidden">
                <div className="absolute w-4 h-4 bg-white/90 rounded-full top-1.5 left-1.5" />
                <div className="absolute w-2 h-2 bg-white/50 rounded-full bottom-2 right-2" />
              </div>
              <span className="text-[22px] font-serif tracking-tight text-gray-900">
                RedMix
              </span>
            </a>
            <ul className="hidden md:flex items-center gap-9 list-none">
              <li>
                <a href="#solutions" className="text-sm text-gray-600 hover:text-gray-900 transition-colors no-underline">
                  解决方案
                </a>
              </li>
              <li>
                <a href="#cases" className="text-sm text-gray-600 hover:text-gray-900 transition-colors no-underline">
                  应用场景
                </a>
              </li>
              <li>
                <a href="#testimonial" className="text-sm text-gray-600 hover:text-gray-900 transition-colors no-underline">
                  客户案例
                </a>
              </li>
              <li>
                <a href="#contact" className="text-sm text-gray-600 hover:text-gray-900 transition-colors no-underline">
                  联系我们
                </a>
              </li>
            </ul>
            <button
              onClick={handleLoginClick}
              className="px-5 py-2.5 bg-[#E85A24] text-white text-sm font-medium rounded-lg cursor-pointer transition-all duration-300 hover:bg-[#FF7A45] hover:-translate-y-0.5 border-none"
            >
              登录
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="min-h-screen flex items-center relative pt-32 pb-24 px-6 bg-white z-10">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-[-30%] right-[-10%] w-[60%] h-full bg-[radial-gradient(ellipse_at_center,rgba(232,90,36,0.06)_0%,transparent_60%)] animate-[float_12s_ease-in-out_infinite]" />
          <div className="absolute bottom-[-20%] left-[-10%] w-[50%] h-[80%] bg-[radial-gradient(ellipse_at_center,rgba(99,102,241,0.04)_0%,transparent_60%)] animate-[float_15s_ease-in-out_infinite_reverse]" />
        </div>
        <div className="container mx-auto px-6 max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div className="max-w-[560px]">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[rgba(232,90,36,0.06)] border border-[rgba(232,90,36,0.15)] rounded-full text-[13px] text-gray-600 mb-7 animate-[fadeUp_0.6s_ease-out]">
                新媒体矩阵运营 · <span className="text-[#E85A24] font-medium">效率提升 300%</span>
              </div>
              <h1 className="font-serif text-[clamp(42px,5.5vw,64px)] font-normal leading-[1.15] tracking-tight mb-6 animate-[fadeUp_0.6s_ease-out_0.1s_both]">
                让品牌在<br />
                <span className="bg-gradient-to-br from-[#E85A24] to-[#FF7A45] bg-clip-text text-transparent">
                  每个触点
                </span>
                都闪耀
              </h1>
              <p className="text-[17px] text-gray-600 leading-relaxed mb-10 animate-[fadeUp_0.6s_ease-out_0.2s_both]">
                一站式新媒体矩阵运营平台。统一管理数百个账号，AI 驱动内容创作，数据洞察增长机会，助力企业构建强大的品牌声量。
              </p>
              <div className="flex gap-4 animate-[fadeUp_0.6s_ease-out_0.3s_both]">
                <button
                  onClick={handleLoginClick}
                  className="inline-flex items-center gap-2.5 px-8 py-4 bg-gradient-to-br from-[#E85A24] to-[#FF7A45] text-white text-[15px] font-medium rounded-[10px] cursor-pointer transition-all duration-300 hover:-translate-y-0.5 shadow-[0_4px_20px_rgba(232,90,36,0.3)] hover:shadow-[0_8px_30px_rgba(232,90,36,0.4)] border-none"
                >
                  免费试用
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                <a
                  href="#solutions"
                  className="inline-flex items-center gap-2.5 px-8 py-4 bg-white text-gray-900 text-[15px] font-medium border border-gray-300 rounded-[10px] cursor-pointer transition-all duration-300 hover:bg-gray-50 hover:border-[#E85A24] hover:text-[#E85A24] no-underline"
                >
                  了解更多
                </a>
              </div>
            </div>
            <div className="relative animate-[fadeUp_0.8s_ease-out_0.4s_both]">
              <div className="bg-white border border-gray-300 rounded-2xl p-5 relative overflow-hidden shadow-[0_4px_24px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)]">
                <div className="flex items-center gap-2 mb-5">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#EF4444]" />
                  <div className="w-2.5 h-2.5 rounded-full bg-[#F59E0B]" />
                  <div className="w-2.5 h-2.5 rounded-full bg-[#22C55E]" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
                    <div className="text-xs text-gray-500 mb-2">本月曝光量</div>
                    <div>
                      <span className="text-[28px] font-semibold">2.8M</span>
                      <span className="inline-flex items-center gap-1 text-[13px] text-green-500 ml-2">
                        ↑ 23%
                      </span>
                    </div>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
                    <div className="text-xs text-gray-500 mb-2">账号矩阵</div>
                    <div className="text-[28px] font-semibold text-[#E85A24]">186</div>
                  </div>
                  <div className="col-span-2 bg-gray-50 border border-gray-200 rounded-xl p-5">
                    <div className="text-xs text-gray-500 mb-2">增长趋势</div>
                    <div className="flex items-end gap-1 h-10 mt-3">
                      {[45, 60, 52, 78, 65, 89, 72, 95, 88, 100].map((height, i) => (
                        <div
                          key={i}
                          className="flex-1 bg-gradient-to-t from-[#E85A24] to-[rgba(232,90,36,0.2)] rounded-[2px] animate-[grow_1s_ease-out_forwards]"
                          style={{ height: `${height}%` }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-20 border-t border-b border-gray-200 bg-white relative z-10 fade-in">
        <div className="container mx-auto px-6 max-w-7xl text-center">
          <div className="text-[13px] text-gray-500 mb-8">已服务超过 500 家企业品牌</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-12">
            {[
              { value: '50万+', desc: '日均内容发布' },
              { value: '10亿+', desc: '累计曝光量' },
              { value: '300%', desc: '平均效率提升' },
              { value: '98%', desc: '客户续费率' },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className="font-serif text-5xl text-gray-900 mb-2">{stat.value}</div>
                <div className="text-sm text-gray-600">{stat.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pain Points Section */}
      <section id="problems" className="py-30 bg-white relative z-10 fade-in">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="text-center mb-16">
            <div className="text-[13px] font-medium text-[#E85A24] tracking-wider uppercase mb-4">
              运营挑战
            </div>
            <h2 className="font-serif text-[clamp(32px,4vw,44px)] font-normal tracking-tight mb-4">
              您是否也在面对这些难题？
            </h2>
            <p className="text-[17px] text-gray-600 max-w-[560px] mx-auto">
              随着新媒体矩阵规模扩大，传统运营方式已难以支撑业务增长
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: '📊', title: '账号多，管理乱', desc: '数十上百个账号分散在不同平台，频繁切换登录，数据无法统一查看，运营效率低下。' },
              { icon: '⏰', title: '内容生产慢', desc: '选题策划、文案撰写、图片设计耗时耗力，难以满足多账号高频发布的内容需求。' },
              { icon: '📈', title: '增长遇到瓶颈', desc: '缺乏数据洞察能力，不清楚什么内容受欢迎，难以制定科学的增长策略。' },
              { icon: '👥', title: '团队协作难', desc: '任务分配、审核流程、进度追踪全靠人工，沟通成本高，容易出错漏发。' },
              { icon: '🎯', title: '竞品动态难追踪', desc: '手动监控竞争对手耗费大量时间，无法及时捕捉市场趋势和热点话题。' },
              { icon: '💰', title: '人力成本高', desc: '规模化运营需要大量人力投入，人员培训和管理成本居高不下。' },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-white border border-gray-300 rounded-2xl p-8 transition-all duration-300 hover:border-[#E85A24] hover:-translate-y-1 hover:shadow-[0_12px_32px_rgba(232,90,36,0.1)] relative z-10"
              >
                <div className="w-12 h-12 bg-[rgba(232,90,36,0.08)] rounded-xl flex items-center justify-center text-[22px] mb-5">
                  {item.icon}
                </div>
                <h3 className="text-lg font-semibold mb-3">{item.title}</h3>
                <p className="text-[15px] text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section id="solutions" className="py-30 bg-gray-50 relative z-10 fade-in">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="text-center mb-16">
            <div className="text-[13px] font-medium text-[#E85A24] tracking-wider uppercase mb-4">
              解决方案
            </div>
            <h2 className="font-serif text-[clamp(32px,4vw,44px)] font-normal tracking-tight mb-4">
              一个平台，解决所有问题
            </h2>
            <p className="text-[17px] text-gray-600 max-w-[560px] mx-auto">
              RedMix 为企业提供端到端的新媒体矩阵运营解决方案
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div className="bg-white border border-gray-300 rounded-[20px] p-8 relative shadow-[0_4px_24px_rgba(0,0,0,0.06)]">
              <div className="bg-gray-50 border border-gray-200 rounded-xl overflow-hidden">
                <div className="p-4 border-b border-gray-200 flex items-center gap-3 bg-white">
                  <div className="w-8 h-8 bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] rounded-full" />
                  <div className="flex-1">
                    <div className="text-sm font-medium">美妆种草日记</div>
                    <div className="text-xs text-gray-500">小红书 · 美妆赛道</div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-px bg-gray-200">
                  {[
                    { value: '58.2K', label: '粉丝数' },
                    { value: '12.6%', label: '互动率' },
                    { value: 'A+', label: '健康度' },
                  ].map((stat, i) => (
                    <div key={i} className="bg-white p-5 text-center">
                      <div className="text-xl font-semibold text-[#E85A24]">{stat.value}</div>
                      <div className="text-xs text-gray-500 mt-1">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div>
              <h3 className="font-serif text-4xl font-normal mb-5">统一账号管理中心</h3>
              <p className="text-base text-gray-600 leading-relaxed mb-8">
                告别繁琐的多账号切换，在一个平台集中管理所有新媒体账号，实时掌握每个账号的运营状态。
              </p>
              <ul className="space-y-3.5 list-none">
                {[
                  { text: '多平台聚合 — 小红书、抖音、微博等主流平台一站接入' },
                  { text: '分组标签管理 — 按赛道、品牌、区域灵活组织账号' },
                  { text: '健康度监控 — 实时预警账号异常，保障运营安全' },
                  { text: '权限精细控制 — 不同角色不同权限，安全高效协作' },
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3.5 py-3.5 border-b border-gray-200 last:border-0">
                    <span className="w-5.5 h-5.5 bg-[rgba(232,90,36,0.12)] rounded-full flex items-center justify-center text-[#E85A24] text-xs flex-shrink-0 mt-0.5">
                      ✓
                    </span>
                    <span className="text-[15px] text-gray-600">
                      <strong className="text-gray-900 font-medium">{item.text.split(' — ')[0]}</strong>
                      {item.text.includes(' — ') && ` — ${item.text.split(' — ')[1]}`}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section id="cases" className="py-30 bg-white relative z-10 fade-in">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="text-center mb-16">
            <div className="text-[13px] font-medium text-[#E85A24] tracking-wider uppercase mb-4">
              应用场景
            </div>
            <h2 className="font-serif text-[clamp(32px,4vw,44px)] font-normal tracking-tight mb-4">
              各行各业的增长引擎
            </h2>
            <p className="text-[17px] text-gray-600 max-w-[560px] mx-auto">
              无论您是什么行业，RedMix 都能帮助您实现新媒体营销目标
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { icon: '💄', title: '美妆个护品牌', desc: '通过矩阵账号布局，多维度种草触达目标用户。AI 智能生成产品测评、使用教程等内容，持续构建品牌口碑。', metrics: [{ value: '5x', label: '内容产出提升' }, { value: '180%', label: '曝光增长' }] },
              { icon: '🏠', title: '家居生活品牌', desc: '打造生活方式内容矩阵，从场景化种草到购买转化。数据驱动选品策略，精准触达家居消费人群。', metrics: [{ value: '320%', label: '获客效率' }, { value: '45%', label: '成本降低' }] },
              { icon: '🍜', title: '餐饮连锁品牌', desc: '各门店账号统一管理，本地化内容差异化运营。结合热点话题快速响应，提升区域品牌影响力。', metrics: [{ value: '200+', label: '门店账号' }, { value: '60%', label: '到店转化' }] },
              { icon: '📚', title: '教育培训机构', desc: '知识干货内容矩阵建设，打造专业权威形象。精准获取目标学员群体，降低招生获客成本。', metrics: [{ value: '80%', label: '线索成本降低' }, { value: '3x', label: '咨询量提升' }] },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-white border border-gray-300 rounded-[20px] p-10 transition-all duration-300 hover:border-[rgba(232,90,36,0.3)] hover:shadow-[0_12px_32px_rgba(232,90,36,0.08)] relative overflow-hidden z-10"
              >
                <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#E85A24] to-[#FF7A45] opacity-0 transition-opacity duration-300 hover:opacity-100" />
                <div className="text-[32px] mb-5">{item.icon}</div>
                <h3 className="text-[22px] font-semibold mb-3">{item.title}</h3>
                <p className="text-[15px] text-gray-600 leading-relaxed mb-6">{item.desc}</p>
                <div className="flex gap-6">
                  {item.metrics.map((metric, j) => (
                    <div key={j} className="flex items-center gap-2">
                      <span className="text-lg font-semibold text-[#E85A24]">{metric.value}</span>
                      <span className="text-[13px] text-gray-500">{metric.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section id="testimonial" className="py-30 bg-gray-50 relative z-10 fade-in">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="max-w-[800px] mx-auto text-center">
            <p className="font-serif text-[clamp(24px,3vw,32px)] font-normal leading-relaxed mb-10 relative">
              <span className="absolute top-[-20px] left-1/2 transform -translate-x-1/2 text-[80px] text-[#E85A24] opacity-30 font-serif">
                "
              </span>
              使用 RedMix 后，我们的新媒体团队从 15 人精简到 5 人，但内容产出量反而提升了 3 倍。这不仅是效率工具，更是业务增长的加速器。
            </p>
            <div className="flex items-center justify-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-[#E85A24] to-[#FF7A45] rounded-full flex items-center justify-center text-xl text-white font-semibold">
                李
              </div>
              <div className="text-left">
                <div className="text-base font-semibold">李明辉</div>
                <div className="text-sm text-gray-500">某美妆品牌 市场总监</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="py-36 relative overflow-hidden z-10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(232,90,36,0.06)_0%,transparent_60%)]" />
        <div className="container mx-auto px-6 max-w-7xl relative z-10 text-center max-w-2xl">
          <h2 className="font-serif text-[clamp(36px,5vw,52px)] font-normal mb-5">
            开启品牌增长新篇章
          </h2>
          <p className="text-[17px] text-gray-600 mb-10">
            现在登录，了解 RedMix 如何帮助您的企业实现新媒体营销目标
          </p>
          <div className="flex gap-4 justify-center">
            <button
              onClick={handleLoginClick}
              className="inline-flex items-center gap-2.5 px-8 py-4 bg-gradient-to-br from-[#E85A24] to-[#FF7A45] text-white text-[15px] font-medium rounded-[10px] cursor-pointer transition-all duration-300 hover:-translate-y-0.5 shadow-[0_4px_20px_rgba(232,90,36,0.3)] hover:shadow-[0_8px_30px_rgba(232,90,36,0.4)] border-none"
            >
              立即登录
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 border-t border-gray-200 bg-white relative z-10">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 bg-gradient-to-br from-[#E85A24] to-[#FF7A45] rounded-lg" />
              <span className="text-lg font-serif">RedMix</span>
            </div>
            <p className="text-sm text-gray-500">© 2024 RedMix. All rights reserved.</p>
            <ul className="flex gap-8 list-none">
              <li><a href="#" className="text-sm text-gray-600 hover:text-gray-900 transition-colors no-underline">产品介绍</a></li>
              <li><a href="#" className="text-sm text-gray-600 hover:text-gray-900 transition-colors no-underline">帮助中心</a></li>
              <li><a href="#" className="text-sm text-gray-600 hover:text-gray-900 transition-colors no-underline">联系我们</a></li>
            </ul>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(20px, -20px) scale(1.05); }
        }
        @keyframes fadeUp {
          from {
            opacity: 0;
            transform: translateY(24px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes grow {
          from { transform: scaleY(0); }
          to { transform: scaleY(1); }
        }
        .fade-in {
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .fade-in.visible {
          opacity: 1;
          transform: translateY(0);
        }
        nav.scrolled {
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border-bottom: 1px solid rgba(0, 0, 0, 0.06);
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        }
      `}</style>
    </div>
  );
}

